export const NETWORK_ERROR_MESSAGE = 'We couldn\'t reach Gradeverse. Check your internet connection and try again.';
export const GENERIC_ERROR_MESSAGE = 'Something went wrong on our side. Please try again — if it keeps happening, contact support.';
export const UNAUTHORIZED_MESSAGE = 'You don\'t have permission to do this. Ask your school admin if you need access.';
export const UNAUTHENTICATED_MESSAGE = 'Your session has expired. Please sign in again.';

const NETWORK_PATTERN = /Failed to fetch|NetworkError|Load failed|ERR_INTERNET|timeout/i;
const TECHNICAL_PATTERN = new RegExp([
  'Variable "\\$',
  'got invalid value',
  'Validation failed for the field',
  'Internal server error',
  'Response not successful',
  'status code 5\\d\\d',
  'SQLSTATE',
  'No query results for model',
  'App\\\\Models',
  '\\b[A-Z]\\w*Exception\\b',
  'Call to (a )?(member|undefined)',
  'Undefined (array key|variable|index)',
  'Cannot query field',
  'is not defined by type',
].join('|'), 'i');
const UNAUTHORIZED_PATTERN = /^(This action is unauthorized\.?|Unauthorized\.?)$/i;
const UNAUTHENTICATED_PATTERN = /^Unauthenticated\.?$/i;

type GraphQLErrorLike = { message?: unknown; extensions?: Record<string, unknown> };
type ErrorLike = {
  message?: unknown;
  graphQLErrors?: GraphQLErrorLike[];
  networkError?: unknown;
};

/** True when the text is internal/technical and must not be shown to users. */
export function isTechnicalMessage(message: string): boolean {
  return TECHNICAL_PATTERN.test(message);
}

/** First Lighthouse `extensions.validation` message, the human-readable rule message. */
export function firstValidationMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  for (const gqlError of (error as ErrorLike).graphQLErrors ?? []) {
    const validation = gqlError?.extensions?.validation;
    if (!validation || typeof validation !== 'object') continue;
    for (const messages of Object.values(validation as Record<string, unknown>)) {
      const first = Array.isArray(messages) ? messages[0] : messages;
      if (typeof first === 'string' && first.length > 0) return first;
    }
  }
  return undefined;
}

function rawMessageOf(error: unknown): string | undefined {
  if (typeof error === 'string') return error || undefined;
  if (typeof error === 'object' && error !== null) {
    const { message } = error as ErrorLike;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return undefined;
}

/**
 * Map a raw message to what the user should see. `fallback` replaces technical text when the caller
 * has a more specific friendly message (e.g. "Failed to save configuration.").
 */
export function friendlyMessage(message: string, fallback?: string): string {
  const trimmed = message.trim();
  if (UNAUTHENTICATED_PATTERN.test(trimmed)) return UNAUTHENTICATED_MESSAGE;
  if (UNAUTHORIZED_PATTERN.test(trimmed)) return UNAUTHORIZED_MESSAGE;
  if (NETWORK_PATTERN.test(trimmed)) return NETWORK_ERROR_MESSAGE;
  if (isTechnicalMessage(trimmed)) return fallback || GENERIC_ERROR_MESSAGE;
  return message;
}

/**
 * Turn any thrown value (ApolloError, Error, string, GraphQL error object) into a message that is safe
 * and useful to show a user. Field validation messages win; network failures, auth failures and
 * technical/internal text are replaced with plain wording, and the original is logged for debugging.
 */
export function toUserMessage(error: unknown, fallback?: string): string {
  const validation = firstValidationMessage(error);
  if (validation) return validation;

  const raw = rawMessageOf(error);
  const hasNetworkError = typeof error === 'object' && error !== null && Boolean((error as ErrorLike).networkError);
  const hasGraphQLErrors = (typeof error === 'object' && error !== null && ((error as ErrorLike).graphQLErrors?.length ?? 0) > 0);

  if (hasNetworkError && !hasGraphQLErrors) {
    console.error(error);
    return NETWORK_ERROR_MESSAGE;
  }

  if (!raw) return fallback || GENERIC_ERROR_MESSAGE;

  const friendly = friendlyMessage(raw, fallback);
  if (friendly !== raw) console.error(error);
  return friendly;
}

/**
 * Narrow an unknown caught value to a human-readable message.
 *
 * Replaces the `catch (e: any)` + `e.message` pattern: with `strict` on, a caught value is `unknown`,
 * so this centralizes the narrowing. Raw technical text is never returned; see toUserMessage.
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (!rawMessageOf(error) && !firstValidationMessage(error)) return fallback;

  return toUserMessage(error, fallback === 'An unexpected error occurred' ? undefined : fallback);
}
